import { Link } from "react-router-dom";
import { Droplets, Zap, Wind, Leaf, Refrigerator, Wifi, Bug, Wrench } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { PageHero, CtaButton, CtaBand, SectionHead, usePageMeta } from "@/components/shared";

const CATEGORIES = [
  [Droplets, "Plumbing", "Leaks, water heaters, pumps and fixtures — handled before they damage."],
  [Zap, "Electrical", "Faults, fittings and safety issues coordinated with licensed electricians."],
  [Wind, "Air Conditioning", "Servicing and urgent repairs — essential in a Bermuda summer."],
  [Leaf, "Landscaping", "Gardens, pools and grounds kept to the standard your photos promise."],
  [Refrigerator, "Appliances", "Kitchen and laundry appliance faults diagnosed and repaired."],
  [Wifi, "Internet & Wi-Fi", "Connectivity issues resolved fast — guests expect it to simply work."],
  [Bug, "Pest Control", "Discreet, scheduled treatment by trusted local providers."],
  [Wrench, "General Repairs", "Everything else a lived-in, loved-in island home eventually needs."],
];

export default function Maintenance() {
  usePageMeta("Maintenance Coordination", "One local contact between Bermuda homeowners and trusted trades — plumbing, electrical, AC, landscaping and general repairs.");
  return (
    <main>
      <PageHero
        overline="Maintenance Coordination"
        lines={["One Call.", "Every Trade. Handled."]}
        copy="We are the central local contact between you and Bermuda's trusted service providers. You never chase a contractor, translate a problem across time zones, or wonder whether the work was actually done."
        image={IMAGES.interior}
      >
        <CtaButton to="/owner/login" variant="accent" testid="maintenance-owner-request-btn">Submit a Maintenance Request</CtaButton>
        <CtaButton to="/assessment" variant="outline" dark testid="maintenance-assessment-btn">Get a Property Assessment</CtaButton>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="What We Coordinate"
          title="Approved local providers, supervised on site."
          copy="We arrange access, oversee the work, verify completion and report back with photographs — so quality is never a matter of trust alone."
        />
        <div className="mt-14 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map(([Icon, title, copy], i) => (
            <Reveal key={title} delay={(i % 4) * 0.06} className="h-full">
              <div className="trace-card h-full bg-white p-8">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ocean-light text-ocean">
                  <Icon size={17} strokeWidth={1.5} />
                </span>
                <h4 className="mt-5 font-serif text-xl text-navy">{title}</h4>
                <p className="mt-2 text-[13px] leading-relaxed text-navy/60">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 border-l-2 border-bermuda bg-sand-light p-8 lg:p-10">
            <h3 className="font-serif text-2xl text-navy">Transparent, owner-controlled spending.</h3>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-navy/70">
              Contractor charges and materials are billed separately at cost — never marked up invisibly. Significant work
              always requires your explicit authorization, except under an emergency procedure we agree with you in writing
              beforehand. Existing owners can set a spending authorization limit on every request through the{" "}
              <Link to="/owner/login" data-testid="maintenance-portal-link" className="text-bermuda-dark underline underline-offset-4 hover:text-navy">Owner Portal</Link>.
            </p>
          </div>
        </Reveal>
      </section>

      <CtaBand title="Something needs attention at your property?" copy="Existing owners can submit a maintenance request through the portal; new owners can begin with a property assessment." />
    </main>
  );
}
